import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AgentTurn } from './agent.service';

export interface NotebookSummary {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  entryCount: number;
  updatedAt: string;
}

export interface NotebookEntryDto {
  id: string;
  notebookId: string;
  notebookTitle: string;
  notebookSlug: string;
  content: string;
  headline: string | null;
  tags: string[];
  createdAt: string;
}

export interface NotebookDetail {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  entries: NotebookEntryDto[];
}

export interface CosToolInvocation {
  name: string;
  argumentsJson: string;
  resultPreview: string | null;
  /** Populated when name === 'recall_notebook_entries' and the search returned hits. */
  recallEntries?: NotebookEntryDto[] | null;
}

export interface CosChatRequest {
  history: AgentTurn[];
  message: string;
  /** Phase 3 ADHD design — soft mode: bias toward notebook capture, soften receipts. */
  stuckMode?: boolean;
}

export interface CosChatResponse {
  reply: string;
  toolInvocations: CosToolInvocation[];
}

/**
 * Wire client for the Chief-of-Staff bar. Posts to /api/cos/chat which goes
 * through ClaudeCosService on the backend; that service runs the tool-loop
 * (capture_signal, capture_notebook_entry, recall_notebook_entries,
 * list_notebooks) and returns the final reply plus what it actually filed.
 *
 * Kept separate from AgentService — the existing /api/agent/chat surface is
 * read-only and shouldn't grow tool routing.
 */
@Injectable({ providedIn: 'root' })
export class CosService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.apiBaseUrl.replace(/\/+$/, '');
  private readonly base = `${this.api}/api/cos`;
  private readonly notebooksBase = `${this.api}/api/notebooks`;

  async chat(req: CosChatRequest): Promise<CosChatResponse> {
    return await firstValueFrom(
      this.http.post<CosChatResponse>(`${this.base}/chat`, req)
    );
  }

  async listNotebooks(): Promise<NotebookSummary[]> {
    return await firstValueFrom(
      this.http.get<NotebookSummary[]>(this.notebooksBase)
    );
  }

  async getNotebook(slug: string): Promise<NotebookDetail> {
    return await firstValueFrom(
      this.http.get<NotebookDetail>(`${this.notebooksBase}/${encodeURIComponent(slug)}`)
    );
  }

  async searchEntries(query: string, slug?: string, limit = 50): Promise<NotebookEntryDto[]> {
    let params = new HttpParams().set('query', query).set('limit', String(limit));
    if (slug) params = params.set('notebookSlug', slug);
    return await firstValueFrom(
      this.http.get<NotebookEntryDto[]>(`${this.notebooksBase}/search`, { params })
    );
  }

  async deleteNotebook(slug: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(`${this.notebooksBase}/${encodeURIComponent(slug)}`)
    );
  }

  async updateEntry(
    entryId: string,
    body: { content: string; headline: string | null; tags: string[] | null },
  ): Promise<NotebookEntryDto> {
    return await firstValueFrom(
      this.http.put<NotebookEntryDto>(
        `${this.notebooksBase}/entries/${encodeURIComponent(entryId)}`,
        body,
      ),
    );
  }

  async deleteEntry(entryId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(`${this.notebooksBase}/entries/${encodeURIComponent(entryId)}`)
    );
  }
}
